import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AccountLogic } from '../account/account.logic';
import { EnvironmentService } from '../environment/environment.service';
import { Account, IGoodieActivateData, IGoodieAdress, IGoodieResponse, IGoodiesDictModel, IGoodiesModel } from '@mwe/models';
import { lastValueFrom } from 'rxjs';
import { GoodieStateService } from './goodie-state.service';
import dayjs from 'dayjs';
import { isArrayWithMinOneItem } from '@mwe/utils';

@Injectable({
  providedIn: 'root',
})
export class GoodieService {
  private http = inject(HttpClient);
  private accountLogic = inject(AccountLogic);
  private environmentService = inject(EnvironmentService);
  private goodieStateService = inject(GoodieStateService);

  private get goodieResourceUrl(): string {
    return `${this.environmentService.getApiUrl()}api/goodies`;
  }

  private get account(): Account {
    return this.accountLogic.getAccount();
  }

  async getSingleGoodieBySlug(slug: string): Promise<IGoodiesModel> {
    const goodieFromState = this.goodieStateService.goodies?.find(g => g.slug === slug);
    if (goodieFromState) {
      return goodieFromState;
    }

    const params = new HttpParams().set('goodieId', slug);
    const { data } = await lastValueFrom(this.http.get<IGoodieResponse<IGoodiesModel[]>>(`${this.goodieResourceUrl}/single`, { params }));

    return data?.[0];
  }

  async activate(id: string, address?: IGoodieAdress): Promise<IGoodieResponse<{ code: string | null }>> {
    const body: IGoodieActivateData = {
      goodieId: id,
      userId: this.account.id,
      email: this.account.email,
      firstName: this.account.firstName,
      lastName: this.account.lastName,
    };

    if (address) {
      body.address = address;
    }

    const resp = await lastValueFrom(
      this.http.post<
        IGoodieResponse<{
          code: string | null;
        }>
      >(`${this.goodieResourceUrl}/activate`, body),
    );

    this.goodieStateService.clearGoodies();
    return resp;
  }

  async checkVoucher(goodieId: string, code: string) {
    const params = new HttpParams().set('goodieId', goodieId).set('code', code);
    return await lastValueFrom(this.http.get(`${this.goodieResourceUrl}/codes/checkcode`, { params }));
  }

  async favorite(id: string, isFavorite: boolean) {
    const cachedGoodies = this.goodieStateService.goodies;

    this.goodieStateService.goodies = cachedGoodies.map(goodie => {
      if (goodie.goodieId === id) {
        return {
          ...goodie,
          isFavorite: !isFavorite,
        };
      }
      return goodie;
    });

    if (isFavorite) {
      return await lastValueFrom(
        this.http.post(`${this.goodieResourceUrl}/unfavorite`, {
          goodieId: id,
          userId: this.account.id,
        }),
      );
    }

    return await lastValueFrom(
      this.http.post(
        `${this.goodieResourceUrl}/favorite`,
        {
          goodieId: id,
          userId: this.account.id,
        },
        { observe: 'response' },
      ),
    );
  }

  async getGoodieCode(goodieId: string): Promise<IGoodieResponse<IGoodiesModel>> {
    const params = new HttpParams().set('goodieId', goodieId);
    return await lastValueFrom(this.http.get<IGoodieResponse<IGoodiesModel>>(`${this.goodieResourceUrl}/code`, { params }));
  }

  async getAllGoodies(): Promise<IGoodieResponse<IGoodiesDictModel>> {
    const sortedGoodies = await this.getGoodies();

    const now = dayjs();
    const mappedData: IGoodiesDictModel = sortedGoodies?.reduce(
      (result, goodie) => {
        // do not display goodies without image or hidden goodies
        if (!goodie.voucher.image || goodie.isHidden) return result;

        const { slug } = goodie.voucher.category;

        if (now.isAfter(goodie.validTill) && goodie.activatedOn) {
          result.expired.goodies.push(goodie);
          return result;
        }

        if (goodie.activatedOn) {
          result.activated.goodies.push(goodie);
          return result;
        }

        if (now.isBefore(goodie.validTill)) {
          const categoryPosition = goodie.voucher.category?.position_category ?? 0;

          // highlight and favorite do not remove goodie out of other categories
          if (goodie.isHighlight) {
            result.highlight.goodies.push(goodie);
          }

          if (goodie.isFavorite) {
            result.favorite.goodies.push(goodie);
          }

          if (!result[slug]) {
            result[slug] = {
              category: { ...goodie.voucher.category, categoryPosition },
              goodies: [goodie],
            };
          } else {
            result[slug].goodies.push(goodie);
          }

          return result;
        }

        return result;
      },
      {
        highlight: {
          category: {
            // title and description get translated in goodies-overview.component.html
            name: '',
            slug: 'highlight',
            description: '',
            position_category: 0,
          },
          goodies: [],
        },
        favorite: {
          category: {
            name: '',
            slug: 'favorite',
            description: '',
            position_category: 0,
          },
          goodies: [],
        },
        activated: {
          category: {
            name: '',
            slug: 'activated',
            description: '',
            position_category: 0,
          },
          goodies: [],
        },
        expired: {
          category: {
            name: '',
            slug: 'expired',
            description: '',
            position_category: 0,
          },
          goodies: [],
        },
      },
    );

    return {
      data: mappedData,
      meta: {},
      errors: [],
    };
  }

  private async getGoodies(): Promise<IGoodiesModel[]> {
    if (isArrayWithMinOneItem(this.goodieStateService.goodies)) {
      return this.goodieStateService.goodies;
    }

    if (this.goodieStateService.requestGoodiesPromise) {
      return this.goodieStateService.requestGoodiesPromise;
    }

    this.goodieStateService.requestGoodiesPromise = this.requestGoodies();
    const goodies = await this.goodieStateService.requestGoodiesPromise;
    this.goodieStateService.goodies = goodies;
    return goodies;
  }

  private async requestGoodies(): Promise<IGoodiesModel[]> {
    const { data: allGoodies } = await lastValueFrom(this.http.get<IGoodieResponse<IGoodiesModel[]>>(`${this.goodieResourceUrl}/all`));
    return allGoodies?.toSorted((a, b) => a.voucher.position - b.voucher.position);
  }
}
